/* eslint no-console: 0 */
import * as types from '../mutation-types';
import Report from '../../api/reports';
import Conversation from '../../api/inbox/conversation';
import { downloadCsvFile, generateFileName } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';
import {
  reconcileHeatmapData,
  clampDataBetweenTimeline,
} from 'shared/helpers/ReportsDataHelper';

const state = {
  fetchingStatus: false,
  accountReport: {
    isFetching: {
      conversations_count: false,
      incoming_messages_count: false,
      outgoing_messages_count: false,
      avg_first_response_time: false,
      avg_resolution_time: false,
      resolutions_count: false,
      bot_resolutions_count: false,
      bot_handoffs_count: false,
      reply_time: false,
    },
    data: {
      conversations_count: [],
      incoming_messages_count: [],
      outgoing_messages_count: [],
      avg_first_response_time: [],
      avg_resolution_time: [],
      resolutions_count: [],
      bot_resolutions_count: [],
      bot_handoffs_count: [],
      reply_time: [],
    },
  },
  accountSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    reply_time: 0,
    resolutions_count: 0,
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  botSummary: {
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  overview: {
    uiFlags: {
      isFetchingAccountConversationMetric: false,
      isFetchingAccountConversationsHeatmap: false,
      isFetchingAgentConversationMetric: false,
      isFetchingAgentConversationDetails: false,
    },
    accountConversationMetric: {},
    accountConversationHeatmap: [],
    agentConversationMetric: [],
    agentConversationDetails: {},
  },
  inbound: {
    uiFlags: { isFetching: false, isFetchingDrilldown: false },
    data: {},
    drilldown: { conversations: [], meta: {} },
  },
  outbound: {
    uiFlags: {
      isFetching: false,
      isFetchingDrilldown: false,
      isFetchingMetaInsights: false,
    },
    data: {},
    drilldown: { conversations: [], meta: {} },
  },
  campaigns: {
    uiFlags: {
      isFetching: false,
      isFetchingDrilldown: false,
      isFetchingMetaInsights: false,
    },
    data: {},
    drilldown: { conversations: [], meta: {} },
  },
};

const getters = {
  getAccountReports(_state) {
    return _state.accountReport;
  },
  getAccountSummary(_state) {
    return _state.accountSummary;
  },
  getBotSummary(_state) {
    return _state.botSummary;
  },
  getAccountConversationMetric(_state) {
    return _state.overview.accountConversationMetric;
  },
  getAccountConversationHeatmapData(_state) {
    return _state.overview.accountConversationHeatmap;
  },
  getAgentConversationMetric(_state) {
    return _state.overview.agentConversationMetric;
  },
  getAgentConversationDetails(_state) {
    return _state.overview.agentConversationDetails;
  },
  getOverviewUIFlags($state) {
    return $state.overview.uiFlags;
  },
  getInboundDashboard(_state) {
    return _state.inbound.data;
  },
  getInboundUIFlags(_state) {
    return _state.inbound.uiFlags;
  },
  getInboundDrilldown(_state) {
    return _state.inbound.drilldown;
  },
  getOutboundDashboard(_state) {
    return _state.outbound.data;
  },
  getOutboundUIFlags(_state) {
    return _state.outbound.uiFlags;
  },
  getOutboundDrilldown(_state) {
    return _state.outbound.drilldown;
  },
  getCampaignsDashboard(_state) {
    return _state.campaigns.data;
  },
  getCampaignsUIFlags(_state) {
    return _state.campaigns.uiFlags;
  },
  getCampaignsDrilldown(_state) {
    return _state.campaigns.drilldown;
  },
};

export const actions = {
  fetchAccountReport({ commit }, reportObj) {
    const { metric } = reportObj;
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
      metric,
      value: true,
    });
    Report.getReports(reportObj).then(accountReport => {
      let { data } = accountReport;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);
      commit(types.default.SET_ACCOUNT_REPORTS, {
        metric,
        data,
      });
      commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
        metric,
        value: false,
      });
    });
  },
  fetchAccountConversationHeatmap({ commit }, reportObj) {
    // Conversation Traffic in overview page elefante
    // reportObj.team_id = 2;
    commit(types.default.TOGGLE_HEATMAP_LOADING, true);
    Report.getReports({ ...reportObj, groupBy: 'hour' }).then(heatmapData => {
      let { data } = heatmapData;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);

      data = reconcileHeatmapData(data);

      commit(types.default.SET_HEATMAP_DATA, data);
      commit(types.default.TOGGLE_HEATMAP_LOADING, false);
    });
  },
  fetchAccountSummary({ commit }, reportObj) {
    Report.getSummary(
      reportObj.from,
      reportObj.to,
      reportObj.type,
      reportObj.id,
      reportObj.groupBy,
      reportObj.businessHours
    )
      .then(accountSummary => {
        commit(types.default.SET_ACCOUNT_SUMMARY, accountSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchBotSummary({ commit }, reportObj) {
    Report.getBotSummary({
      from: reportObj.from,
      to: reportObj.to,
      groupBy: reportObj.groupBy,
      businessHours: reportObj.businessHours,
    })
      .then(botSummary => {
        commit(types.default.SET_BOT_SUMMARY, botSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchAccountConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type, 1, reportObj.team_id)
      .then(accountConversationMetric => {
        commit(
          types.default.SET_ACCOUNT_CONVERSATION_METRIC,
          accountConversationMetric.data
        );
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchAgentConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type, reportObj.team, reportObj.page)
      .then(agentConversationMetric => {
        commit(
          types.default.SET_AGENT_CONVERSATION_METRIC,
          agentConversationMetric.data
        );
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      });
  },

  fetchAgentConversationDetails({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_CONVERSATION_DETAILS_LOADING, true);
    Conversation.filter(reportObj)
      .then(agentConversationDetails => {
        commit(
          types.default.SET_AGENT_CONVERSATION_DETAILS,
          agentConversationDetails.data
        );
        commit(types.default.TOGGLE_AGENT_CONVERSATION_DETAILS_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_AGENT_CONVERSATION_DETAILS_LOADING, false);
      });
  },

  downloadAgentReports(_, reportObj) {
    return Report.getAgentReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'agent',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadAgentReportsConversationDetails(_, reportObj) {
    return Conversation.filterExportCSV(reportObj.data)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'agent',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadLabelReports(_, reportObj) {
    return Report.getLabelReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'label',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadInboxReports(_, reportObj) {
    return Report.getInboxReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'inbox',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadTeamReports(_, reportObj) {
    return Report.getTeamReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'team',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadAccountConversationHeatmap(_, reportObj) {
    Report.getConversationTrafficCSV()
      .then(response => {
        downloadCsvFile(
          generateFileName({
            type: 'Conversation traffic',
            to: reportObj.to,
          }),
          response.data
        );

        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'conversation_heatmap',
          businessHours: false,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  async fetchInboundDashboard({ commit }, payload) {
    commit(types.default.SET_INBOUND_UI_FLAG, { isFetching: true });
    try {
      const { data } = await Report.getInboundDashboard(payload);
      commit(types.default.SET_INBOUND_DASHBOARD, data);
    } finally {
      commit(types.default.SET_INBOUND_UI_FLAG, { isFetching: false });
    }
  },

  async fetchInboundDrilldown({ commit }, payload) {
    commit(types.default.SET_INBOUND_UI_FLAG, { isFetchingDrilldown: true });
    try {
      const { data } = await Report.getInboundDrilldown(payload);
      commit(types.default.SET_INBOUND_DRILLDOWN, data);
    } finally {
      commit(types.default.SET_INBOUND_UI_FLAG, { isFetchingDrilldown: false });
    }
  },

  async fetchOutboundDashboard({ commit }, payload) {
    commit(types.default.SET_OUTBOUND_UI_FLAG, { isFetching: true });
    try {
      const { data } = await Report.getOutboundDashboard(payload);
      commit(types.default.SET_OUTBOUND_DASHBOARD, data);
    } finally {
      commit(types.default.SET_OUTBOUND_UI_FLAG, { isFetching: false });
    }
  },

  async fetchOutboundDrilldown({ commit }, payload) {
    commit(types.default.SET_OUTBOUND_UI_FLAG, { isFetchingDrilldown: true });
    try {
      const { data } = await Report.getOutboundDrilldown(payload);
      commit(types.default.SET_OUTBOUND_DRILLDOWN, data);
    } finally {
      commit(types.default.SET_OUTBOUND_UI_FLAG, {
        isFetchingDrilldown: false,
      });
    }
  },

  async downloadOutboundExport(_ctx, { fileName, ...payload }) {
    const response = await Report.getOutboundExport(payload);
    downloadCsvFile(fileName, response.data);
    AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
      reportType: 'outbound',
      businessHours: false,
    });
  },

  async downloadInboundExport(_ctx, { fileName, ...payload }) {
    const response = await Report.getInboundExport(payload);
    downloadCsvFile(fileName, response.data);
    AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
      reportType: 'inbound',
      businessHours: false,
    });
  },

  async fetchCampaignsDashboard({ commit }, payload) {
    commit(types.default.SET_CAMPAIGNS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await Report.getCampaignSummary(payload);
      commit(types.default.SET_CAMPAIGNS_DASHBOARD, data);
    } finally {
      commit(types.default.SET_CAMPAIGNS_UI_FLAG, { isFetching: false });
    }
  },

  async fetchTemplatePerformance({ commit }, payload) {
    commit(types.default.SET_CAMPAIGNS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await Report.getTemplatePerformance(payload);
      return data;
    } finally {
      commit(types.default.SET_CAMPAIGNS_UI_FLAG, { isFetching: false });
    }
  },

  async fetchCampaignContactList({ commit }, payload) {
    commit(types.default.SET_CAMPAIGNS_UI_FLAG, { isFetchingDrilldown: true });
    try {
      const { data } = await Report.getCampaignContactList(payload);
      commit(types.default.SET_CAMPAIGNS_DRILLDOWN, data);
    } finally {
      commit(types.default.SET_CAMPAIGNS_UI_FLAG, { isFetchingDrilldown: false });
    }
  },

  async downloadCampaignsExport(_ctx, payload) {
    await Report.exportCampaignCSV(payload);
  },
  clearCampaignsDashboard({ commit }) {
    commit(types.default.CLEAR_CAMPAIGNS_DASHBOARD);
  },

  async fetchOutboundMetaInsights({ commit }, payload = {}) {
    commit(types.default.SET_OUTBOUND_UI_FLAG, {
      isFetchingMetaInsights: true,
    });
    try {
      const { data } = await Report.getOutboundMetaInsights(payload);
      return data;
    } finally {
      commit(types.default.SET_OUTBOUND_UI_FLAG, {
        isFetchingMetaInsights: false,
      });
    }
  },

  async fetchCampaignsDrilldown({ commit }, payload) {
    commit(types.default.SET_CAMPAIGNS_UI_FLAG, { isFetchingDrilldown: true });
    try {
      const { data } = await Report.getCampaignsDrilldown(payload);
      commit(types.default.SET_CAMPAIGNS_DRILLDOWN, data);
    } finally {
      commit(types.default.SET_CAMPAIGNS_UI_FLAG, {
        isFetchingDrilldown: false,
      });
    }
  },

  async fetchCampaignsMetaInsights({ commit }, payload = {}) {
    commit(types.default.SET_CAMPAIGNS_UI_FLAG, {
      isFetchingMetaInsights: true,
    });
    try {
      const { data } = await Report.getCampaignsMetaInsights(payload);
      return data;
    } finally {
      commit(types.default.SET_CAMPAIGNS_UI_FLAG, {
        isFetchingMetaInsights: false,
      });
    }
  },
};

const mutations = {
  [types.default.SET_ACCOUNT_REPORTS](_state, { metric, data }) {
    _state.accountReport.data[metric] = data;
  },
  [types.default.SET_HEATMAP_DATA](_state, heatmapData) {
    _state.overview.accountConversationHeatmap = heatmapData;
  },
  [types.default.TOGGLE_ACCOUNT_REPORT_LOADING](_state, { metric, value }) {
    _state.accountReport.isFetching[metric] = value;
  },
  [types.default.TOGGLE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationsHeatmap = flag;
  },
  [types.default.SET_ACCOUNT_SUMMARY](_state, summaryData) {
    _state.accountSummary = summaryData;
  },
  [types.default.SET_BOT_SUMMARY](_state, summaryData) {
    _state.botSummary = summaryData;
  },
  [types.default.SET_ACCOUNT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.accountConversationMetric = metricData;
  },
  [types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationMetric = flag;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_DETAILS_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationDetails = flag;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.agentConversationMetric = metricData;
  },
  [types.default.SET_AGENT_CONVERSATION_DETAILS](_state, reportData) {
    _state.overview.agentConversationDetails = reportData;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationMetric = flag;
  },
  [types.default.SET_INBOUND_DASHBOARD](_state, data) {
    _state.inbound.data = data;
  },
  [types.default.SET_INBOUND_DRILLDOWN](_state, data) {
    _state.inbound.drilldown = data;
  },
  [types.default.SET_INBOUND_UI_FLAG](_state, flags) {
    _state.inbound.uiFlags = { ..._state.inbound.uiFlags, ...flags };
  },

  [types.default.SET_OUTBOUND_DASHBOARD](_state, data) {
    _state.outbound.data = data;
  },
  [types.default.SET_OUTBOUND_DRILLDOWN](_state, data) {
    _state.outbound.drilldown = data;
  },
  [types.default.SET_OUTBOUND_UI_FLAG](_state, flags) {
    _state.outbound.uiFlags = { ..._state.outbound.uiFlags, ...flags };
  },

  [types.default.SET_CAMPAIGNS_DASHBOARD](_state, data) {
    _state.campaigns.data = data;
  },
  [types.default.SET_CAMPAIGNS_DRILLDOWN](_state, data) {
    _state.campaigns.drilldown = data;
  },
  [types.default.SET_CAMPAIGNS_UI_FLAG](_state, flags) {
    _state.campaigns.uiFlags = { ..._state.campaigns.uiFlags, ...flags };
  },
  [types.default.CLEAR_CAMPAIGNS_DASHBOARD](_state) {
    _state.campaigns.data = {};
    _state.campaigns.drilldown = { conversations: [], meta: {} };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
